/**
 * The 3D library (docs/17-asset-library/13-library-section.md §2, §3, §8,
 * §9, §11): the list behind the grid at `/assets`, the detail behind an
 * inspector page, the writes on the organisation's own things, and its
 * favourites.
 */
import {
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { api, apiForm } from "./client";
import { MEDIA_KEY } from "./media";
import { ME_KEY } from "./me";
import type { AssetDetail, AssetKind, AssetList } from "./types";

export const ASSETLIB_KEY = ["assetlib"] as const;
export const ASSET_KEY = (kind: AssetKind, id: string) =>
  ["assetlib", kind, id] as const;

export interface AssetFilters {
  /** `object` is objects and looks together; `figure` the People (P29). */
  kind?: "object" | "shape" | "variant" | "material" | "skin" | "figure";
  q?: string;
  tags?: string[];
  tier?: "curated" | "org";
  favorites?: boolean;
  /** One category of `assets/categories.ts`. */
  category?: string;
  /** Default 30. */
  limit?: number;
}

/** `GET /v1/assetlib`, paged by cursor. Absent values, and `favorites` when
 *  false, are left out of the query string. */
export function useAssetLibrary(filters: AssetFilters) {
  return useInfiniteQuery({
    queryKey: [...ASSETLIB_KEY, "list", filters] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.kind) params.set("kind", filters.kind);
      if (filters.q) params.set("q", filters.q);
      for (const tag of filters.tags ?? []) params.append("tag", tag);
      if (filters.tier) params.set("tier", filters.tier);
      if (filters.favorites) params.set("favorites", "true");
      if (filters.category) params.set("category", filters.category);
      if (filters.limit) params.set("limit", String(filters.limit));
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();
      return api<AssetList>(`/v1/assetlib${query ? `?${query}` : ""}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** Whether the organisation has anything of its own in the 3D library:
 *  `undefined` until the answer is in (and when it could not be had). A write
 *  invalidates it with the rest of `ASSETLIB_KEY`. */
export function useOwnsAnything(): boolean | undefined {
  const query = useQuery({
    queryKey: [...ASSETLIB_KEY, "own-any"] as const,
    queryFn: () => api<AssetList>("/v1/assetlib?tier=org&limit=1"),
  });
  return query.data ? query.data.items.length > 0 : undefined;
}

/** The query of one asset's detail: the same key and fetcher wherever it is
 *  asked for, so nothing is fetched twice. The addresses in a detail last
 *  fifteen minutes; it is fresh for ten. */
export function assetQuery(kind: AssetKind, id: string, version?: number) {
  return {
    queryKey:
      version === undefined
        ? ASSET_KEY(kind, id)
        : [...ASSET_KEY(kind, id), version],
    queryFn: () =>
      api<AssetDetail>(
        `/v1/assetlib/${kind}/${encodeURIComponent(id)}${version === undefined ? "" : `?version=${version}`}`,
      ),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  } as const;
}

/** `GET /v1/assetlib/{kind}/{id}`. `onExpired` asks for the detail again,
 *  once for a given asset: the viewer calls it when a model or a picture
 *  could not be fetched, because its address may have run out. */
export function useAsset(
  kind: AssetKind,
  id: string,
  version?: number,
): UseQueryResult<AssetDetail> & { onExpired(): void } {
  const query = useQuery(assetQuery(kind, id, version));
  const asked = useRef(false);
  useEffect(() => {
    asked.current = false;
  }, [kind, id, version]);
  const { refetch } = query;
  const onExpired = useCallback(() => {
    if (asked.current) return;
    asked.current = true;
    void refetch();
  }, [refetch]);
  return { ...query, onExpired };
}

/** After a write: the grid and every detail, the files page's figures and
 *  the storage figures of the header. */
function useAfterWrite() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ASSETLIB_KEY });
    void queryClient.invalidateQueries({ queryKey: MEDIA_KEY });
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  };
}

export interface Saved<K extends AssetKind = AssetKind> {
  kind: K;
  id: string;
  version: number;
}

export interface NewSkin {
  name: string;
  upload_id: string;
  region_type: string;
  emissive: boolean;
  fit?: { aspect: number };
}

/** `POST /v1/assetlib/skins`: one of the user's images becomes an artwork. */
export function useCreateSkin() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (body: NewSkin) =>
      api<Saved<"skin">>("/v1/assetlib/skins", { method: "POST", body }),
    onSuccess: after,
  });
}

/** `POST /v1/assetlib/variants`: the form holds `document` (a JSON string)
 *  and, when the browser could draw one, the picture `thumbnail`. */
export function useSaveLook() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (form: FormData) =>
      apiForm<Saved<"variant">>("/v1/assetlib/variants", form),
    onSuccess: after,
  });
}

export interface DeletedAsset {
  deleted: true;
  still_used_by_jobs: number;
  also_deleted: { kind: AssetKind; id: string; name: string }[];
}

/** `DELETE /v1/assetlib/{kind}/{id}`: one of the organisation's own. */
export function useDeleteAsset() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (asset: { kind: AssetKind; id: string }) =>
      api<DeletedAsset>(
        `/v1/assetlib/${asset.kind}/${encodeURIComponent(asset.id)}`,
        { method: "DELETE" },
      ),
    onSuccess: after,
  });
}

export interface AssetChanges {
  name?: string;
  description?: string;
  tags?: string[];
  category?: string;
}

/** `PATCH /v1/assetlib/{kind}/{id}`: a new version with other details. */
export function usePatchAsset() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (edit: {
      kind: AssetKind;
      id: string;
      changes: AssetChanges;
    }) =>
      api<Saved>(`/v1/assetlib/${edit.kind}/${encodeURIComponent(edit.id)}`, {
        method: "PATCH",
        body: edit.changes,
      }),
    onSuccess: after,
  });
}

/** `PUT` or `DELETE /v1/assetlib/{kind}/{id}/favorite`: the organisation's
 *  star on anything it sees, `on` being the state asked for. Afterwards the
 *  grid and every detail are asked again. */
export function useFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (star: { kind: AssetKind; id: string; on: boolean }) =>
      api<void>(
        `/v1/assetlib/${star.kind}/${encodeURIComponent(star.id)}/favorite`,
        { method: star.on ? "PUT" : "DELETE" },
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ASSETLIB_KEY }),
  });
}
